import React, { useState, useRef, useMemo, useEffect, forwardRef } from 'react'
import ReactPlayer from 'react-player'
import { useKeyPress, useDoubleClick } from 'hooks'
import CustomSlider from './CustomSlider'
import { ProgressRing } from 'components/Zui'
import './player.css'

const Player = (
    {
        url,
        token,
        id,
        onPrevFile,
        onNextFile,
        defaultFullscreen,
        onToggleFullscreen,
        pauseTimer,
        resetTimer,
    },
    ref
) => {
    const [state, setState] = useState({
        playing: false,
        muted: true,
        volume: 0.0,
        playbackRate: 1.0,
        buffering: true,
        played: 0,
        ended: false,
        seeking: false,
        duration: 0,
        url,
    })

    const rightArrowkeyPressed = useKeyPress('ArrowRight')
    const leftArrowkeyPressed = useKeyPress('ArrowLeft')
    const spacekeyPressed = useKeyPress(' ')
    const playerWrprRef = useRef(null)
    const playerRef = useRef(null)

    const { playing, muted, volume, playbackRate, played, duration, ended, buffering } = state

    const bookmarks = useMemo(() => [220, 280, 305, 390], [])

    useEffect(() => {
        setState(prevState => {
            return { ...prevState, url: null }
        })
    }, [id])

    useEffect(() => {
        if (!state.url) {
            setState(prevState => {
                return { ...prevState, played: 0, url, buffering: true }
            })
        }
    }, [state.url, url])

    useEffect(() => {
        if (spacekeyPressed) {
            setState(prevState => {
                return { ...prevState, playing: !prevState.playing, ended: false }
            })
        }
    }, [spacekeyPressed])

    useEffect(() => {
        if (rightArrowkeyPressed) {
            onNextFile()
        }
    }, [rightArrowkeyPressed])

    useEffect(() => {
        if (leftArrowkeyPressed) {
            onPrevFile()
        }
    }, [leftArrowkeyPressed])

    const handlePlayPause = () => {
        if (!playing) {
            pauseTimer()
        } else {
            resetTimer()
        }
        setState(prevState => {
            return { ...prevState, playing: !prevState.playing }
        })
    }

    const handlePlay = () => {
        pauseTimer()
        setState(prevState => {
            return { ...prevState, playing: true, ended: false }
        })
    }

    const handleRewind = () => {
        setState(prevState => {
            return { ...prevState, playing: false }
        })
        playerRef.current.seekTo(playerRef.current.getCurrentTime() - 0.04)
    }

    const handleForward = () => {
        setState(prevState => {
            return { ...prevState, playing: false }
        })
        playerRef.current.seekTo(playerRef.current.getCurrentTime() + 0.04)
    }

    const handleDuration = duration => {
        setState(prevState => {
            return { ...prevState, duration }
        })
    }

    const handleMute = () => {
        setState(prevState => {
            return { ...prevState, muted: !prevState.muted }
        })
    }

    const handleVolumeChange = (e, newValue) => {
        setState(prevState => {
            return {
                ...prevState,
                volume: parseFloat(newValue / 100),
                muted: newValue === 0 ? true : false,
            }
        })
    }

    const handlePlaybackRateChange = playbackRate => {
        setState(prevState => {
            return { ...prevState, playbackRate }
        })
    }

    const handleProgress = changeState => {
        if (!state.seeking) {
            setState(prevState => {
                return {
                    ...prevState,
                    ...changeState,
                }
            })
        }
    }

    const handleSeekChange = (e, value) => {
        setState(prevState => {
            return {
                ...prevState,
                played: parseFloat(value / 100),
            }
        })
    }

    const handleOnSeekMouseDown = e => {
        setState(prevState => {
            return {
                ...prevState,
                seeking: true,
            }
        })
    }

    const handleOSeekMouseUp = (e, value) => {
        const end = ended && played < 1 ? false : ended

        setState(prevState => {
            return {
                ...prevState,
                seeking: false,
                ended: end,
            }
        })
        playerRef.current.seekTo(value / 100)
    }

    const createMarkers = useMemo(() => {
        let marks = []

        if (duration) {
            marks = bookmarks.map(bookmark => {
                const val = (bookmark / duration) * 100
                return val
            })
        }
        return marks
    }, [bookmarks, duration])

    const handleBookmarkNext = () => {
        const current = playerRef.current.getCurrentTime()
        let fd = bookmarks.find(b => b > current)
        if (fd - current <= 7) {
            fd = bookmarks.find(b => b > fd)
        }
        if (fd) {
            playerRef.current.seekTo(fd - 7)
        }
    }

    const handleBookmarkPrev = () => {
        const current = playerRef.current.getCurrentTime()
        const fd = bookmarks.filter(b => b < current).at(-1)
        if (fd) {
            playerRef.current.seekTo(fd - 7)
        }
    }

    const handleBookmarkClick = (val, icon = false) => {
        const time = icon ? 7 : 0
        playerRef.current.seekTo((val * duration) / 100 - time)
    }

    useDoubleClick({
        ref: playerWrprRef,
        onSingleClick: () => handlePlayPause(),
        onDoubleClick: () => onToggleFullscreen(),
    })

    return (
        <div className="video-viewer">
            <div ref={playerWrprRef} className="player-wrapper">
                {buffering ? (
                    <div className="player-loader">
                        <ProgressRing size="s24" />
                    </div>
                ) : (
                    ''
                )}
                <ReactPlayer
                    ref={playerRef}
                    width="auto%"
                    height="100%"
                    url={state.url}
                    playing={playing}
                    muted={muted}
                    volume={volume}
                    playbackRate={playbackRate}
                    onProgress={handleProgress}
                    onDuration={e => handleDuration(e)}
                    onReady={() =>
                        setState(prevState => {
                            return {
                                ...prevState,
                                buffering: false,
                            }
                        })
                    }
                    onBuffer={() => {
                        setState(prevState => {
                            return {
                                ...prevState,
                                buffering: true,
                            }
                        })
                    }}
                    onBufferEnd={() => {
                        setState(prevState => {
                            return {
                                ...prevState,
                                buffering: false,
                            }
                        })
                    }}
                    onEnded={() => {
                        setState({
                            ...state,
                            ended: true,
                            playing: false,
                        })
                        resetTimer()
                    }}
                    config={{
                        file: {
                            forceHLS: true,
                            hlsOptions: {
                                debug: false,
                                xhrSetup: function (xhr, url) {
                                    if (url.toLocaleLowerCase().indexOf('?kid') !== -1) {
                                        xhr.setRequestHeader('Authorization', `Bearer=${token}`)
                                    }
                                },
                            },
                        },
                    }}
                />
            </div>
            <CustomSlider
                // ref={controlRef}
                ref={ref}
                played={played}
                playing={playing}
                muted={muted}
                ended={ended}
                volume={volume}
                buffering={buffering}
                playbackRate={playbackRate}
                duration={duration}
                bookmarks={createMarkers}
                onPlayPause={handlePlayPause}
                onMute={handleMute}
                onRewindFrame={handleRewind}
                onForwardFrame={handleForward}
                onVolumeChange={handleVolumeChange}
                onHandlePlay={handlePlay}
                onVolumeSeekUp={handleVolumeChange}
                onPlaybackChange={handlePlaybackRateChange}
                onBookmarkNext={handleBookmarkNext}
                onBookmarkPrev={handleBookmarkPrev}
                onBookmarkClick={handleBookmarkClick}
                onSeek={handleSeekChange}
                onSeekMouseDown={handleOnSeekMouseDown}
                onSeekMouseUp={handleOSeekMouseUp}
                isFullscreen={defaultFullscreen}
                onToggleFullscreen={onToggleFullscreen}
            />
        </div>
    )
}

export default React.memo(forwardRef(Player))
